<template>
  <Dialog
    :open="open"
    @update:open="$emit('update:open', $event)"
    :title="`Manage Tools — ${server?.display_name ?? ''}`"
    size="lg"
  >
    <div v-if="server" class="space-y-5">
      <!-- Attached tools -->
      <div>
        <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          Attached tools
          <span class="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
            ({{ localMappings.length }})
          </span>
        </h4>

        <div v-if="isLoadingMappings" class="text-xs text-gray-500 dark:text-gray-400 py-3 text-center">
          Loading…
        </div>
        <div
          v-else-if="localMappings.length === 0"
          class="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-4 py-3 text-xs text-gray-500 dark:text-gray-400 text-center"
        >
          No tools attached yet — pick from available tools below
        </div>
        <ul v-else class="space-y-1">
          <li
            v-for="m in localMappings"
            :key="m.tool_id"
            class="flex items-center justify-between rounded bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs"
          >
            <div class="flex items-center gap-2 min-w-0">
              <span
                :class="[
                  'shrink-0 px-1.5 py-0.5 rounded text-xs font-medium',
                  m.tool_type === 'base'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
                ]"
              >{{ m.tool_type }}</span>
              <span class="truncate font-medium text-gray-900 dark:text-gray-100">
                {{ toolName(m.tool_id ?? '') }}
              </span>
              <span class="text-gray-500 dark:text-gray-400 truncate hidden sm:block">
                {{ toolDescription(m.tool_id ?? '') }}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              :disabled="isSaving"
              class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 shrink-0"
              @click="removeTool(m)"
            >
              Remove
            </Button>
          </li>
        </ul>
      </div>

      <div class="border-t border-gray-200 dark:border-gray-700" />

      <!-- Available tools picker -->
      <div>
        <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">Available tools</h4>

        <!-- Sub-tabs: Base / Custom -->
        <div class="flex gap-4 border-b border-gray-200 dark:border-gray-700 mb-3">
          <button
            v-for="tab in availableTabs"
            :key="tab.key"
            @click="pickerTab = tab.key"
            :class="[
              'pb-2 text-xs font-medium border-b-2 -mb-px',
              pickerTab === tab.key
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
            ]"
          >
            {{ tab.label }} ({{ tab.key === 'base' ? filteredBase.length : filteredCustom.length }})
          </button>
        </div>

        <input
          v-model="search"
          type="text"
          placeholder="Search tools…"
          class="w-full mb-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-xs text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />

        <div class="max-h-52 overflow-y-auto space-y-1">
          <template v-if="pickerTab === 'base'">
            <div
              v-if="filteredBase.length === 0"
              class="text-xs text-gray-500 dark:text-gray-400 py-2 text-center"
            >
              {{ search ? 'No matches' : 'All base tools already attached' }}
            </div>
            <button
              v-for="tool in filteredBase"
              :key="tool.id"
              type="button"
              :disabled="isSaving"
              class="w-full flex items-center justify-between text-left rounded px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent hover:border-gray-200 dark:hover:border-gray-700 transition-colors"
              @click="addTool(tool.id, 'base')"
            >
              <div class="min-w-0">
                <span class="font-medium text-gray-900 dark:text-gray-100 block truncate">{{ tool.name }}</span>
                <span class="text-gray-500 dark:text-gray-400 block truncate">{{ tool.description }}</span>
              </div>
              <span class="ml-3 shrink-0 text-blue-600 dark:text-blue-400 font-medium">+ Add</span>
            </button>
          </template>
          <template v-else>
            <div
              v-if="filteredCustom.length === 0"
              class="text-xs text-gray-500 dark:text-gray-400 py-2 text-center"
            >
              {{ search ? 'No matches' : 'All custom tools already attached' }}
            </div>
            <button
              v-for="tool in filteredCustom"
              :key="tool.id"
              type="button"
              :disabled="isSaving"
              class="w-full flex items-center justify-between text-left rounded px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent hover:border-gray-200 dark:hover:border-gray-700 transition-colors"
              @click="addTool(tool.id, 'custom')"
            >
              <div class="min-w-0">
                <span class="font-medium text-gray-900 dark:text-gray-100 block truncate">{{ tool.name }}</span>
                <span class="text-gray-500 dark:text-gray-400 block truncate">{{ tool.description }}</span>
              </div>
              <span class="ml-3 shrink-0 text-purple-600 dark:text-purple-400 font-medium">+ Add</span>
            </button>
          </template>
        </div>
      </div>

      <div class="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
        <Button variant="outline" @click="$emit('update:open', false)">Done</Button>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { Button, Dialog } from '@asabdev/asab-ui';
  import { useMcpVServersStore } from '@/stores/mcpVServers';
  import { useMcpToolsStore } from '@/stores/mcpTools';
  import type { VirtualServer, ToolMapping } from '@/lib/mcp/types';

  interface Props {
    open: boolean;
    server: VirtualServer | null;
    orgId: string;
  }

  const props = defineProps<Props>();
  defineEmits<{ (e: 'update:open', v: boolean): void }>();

  const vserversStore = useMcpVServersStore();
  const toolsStore = useMcpToolsStore();

  const localMappings = ref<ToolMapping[]>([]);
  const isLoadingMappings = ref(false);
  const isSaving = ref(false);
  const search = ref('');
  const pickerTab = ref<'base' | 'custom'>('base');

  const availableTabs = [
    { key: 'base' as const, label: 'Base' },
    { key: 'custom' as const, label: 'Custom' },
  ];

  // Load mappings when modal opens
  watch(
    () => props.open,
    async (open) => {
      if (open && props.server) {
        search.value = '';
        isLoadingMappings.value = true;
        try {
          const ms = await vserversStore.fetchToolMappings(props.orgId, props.server.id);
          localMappings.value = ms.map((m) => ({ tool_id: m.tool_id, tool_type: m.tool_type }));
        } catch {
          localMappings.value = [];
        } finally {
          isLoadingMappings.value = false;
        }
      }
    },
  );

  // Tool name/description lookup helpers
  const toolName = (id: string) =>
    toolsStore.baseTools.find((t) => t.id === id)?.name ??
    toolsStore.customTools.find((t) => t.id === id)?.name ??
    id;

  const toolDescription = (id: string) =>
    toolsStore.baseTools.find((t) => t.id === id)?.description ??
    toolsStore.customTools.find((t) => t.id === id)?.description ??
    '';

  const mappedIds = computed(() => new Set(localMappings.value.map((m) => m.tool_id)));

  const filteredBase = computed(() => {
    const q = search.value.toLowerCase();
    return toolsStore.baseTools.filter(
      (t) =>
        !mappedIds.value.has(t.id) &&
        (!q || t.name.toLowerCase().includes(q) || (t.description ?? '').toLowerCase().includes(q)),
    );
  });

  const filteredCustom = computed(() => {
    const q = search.value.toLowerCase();
    return toolsStore.customTools.filter(
      (t) =>
        !mappedIds.value.has(t.id) &&
        (!q || t.name.toLowerCase().includes(q) || (t.description ?? '').toLowerCase().includes(q)),
    );
  });

  async function addTool(toolId: string, toolType: 'base' | 'custom') {
    if (!props.server) return;
    const updated: ToolMapping[] = [...localMappings.value, { tool_id: toolId, tool_type: toolType }];
    await sync(updated);
  }

  async function removeTool(mapping: ToolMapping) {
    if (!props.server) return;
    const updated = localMappings.value.filter((m) => m.tool_id !== mapping.tool_id);
    await sync(updated);
  }

  async function sync(mappings: ToolMapping[]) {
    if (!props.server) return;
    isSaving.value = true;
    try {
      await vserversStore.setToolMappings(props.orgId, props.server.id, mappings);
      localMappings.value = mappings;
    } catch {
      // store handles error notifications
    } finally {
      isSaving.value = false;
    }
  }
</script>
